import React, { isValidElement } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import Accordion from './blocks/accordion/Accordion';
import AnimatedHero from './blocks/AnimatedHero';
import BreadcrumbsBlock from './blocks/BreadcrumbsBlock';
import CardSlider from './blocks/CardSlider';
import CaseStudy from './blocks/CaseStudy';
import Cta from './blocks/Cta';
import CtaChecklist from './blocks/CtaChecklist';
import DeveloperArea from './blocks/DeveloperArea';
import TrialFormArea from './blocks/TrialFormArea';
import DownloadCards from './blocks/DownloadCards';
import DownloadTabs from './blocks/download-tabs/DownloadTabs';
import FeaturedCards from './blocks/FeaturedCards';
import FeaturedContent from './blocks/FeaturedContent';
import FeaturedResources from './blocks/featured-resources/FeaturedResources';
import FeaturesTableBlock from './blocks/FeaturesTableBlock';
import Header from './blocks/Header';
import Hero from './blocks/hero/Hero';
import IconCards from './blocks/IconCards';
import IconColumns from './blocks/IconColumns';
import InlineNav from './blocks/InlineNav';
import LinkCards from './blocks/link-cards/LinkCards';
import List from './blocks/List';
import LogoList from './blocks/LogoList';
import MediaTabs from './blocks/MediaTabs';
import MiniCta from './blocks/MiniCta';
import MiniHeader from './blocks/MiniHeader';
import OffsetCards from './blocks/OffsetCards';
import Overlay from './blocks/Overlay';
import OverviewTabs from './blocks/OverviewTabs';
import Personalization from './Personalization';
import PhotoHeader from './blocks/PhotoHeader';
import PricingTabs from './blocks/pricing-tabs/PricingTabs';
import PricingTable from './blocks/pricing-table/PricingTable';
import PromoBanner from './blocks/PromoBanner';
import ResourceLinks from './blocks/ResourceLinks';
import ResourceRows from './blocks/ResourceRows';
import ScrollSync from './blocks/scroll-sync/ScrollSync';
import SocialProof from './blocks/SocialProof';
import SplitHero from './blocks/SplitHero';
import StatsBadges from './blocks/StatsBadges';
import StatGrid from './blocks/StatGrid';
import StackedHeader from './blocks/StackedHeader';
import Tabs from './blocks/Tabs';
import Testimonials from './blocks/Testimonials';
import Text from './blocks/Text';
import TextMedia from './blocks/TextMedia';
import TwoColumnText from './blocks/TwoColumnText';
import VerticalTabs from './blocks/vertical-tabs/VerticalTabs';

const blockComponents = {
  ContentfulAccordion: Accordion,
  ContentfulAnimatedHero: AnimatedHero,
  ContentfulBreadcrumbs: BreadcrumbsBlock,
  ContentfulCardSlider: CardSlider,
  ContentfulCaseStudy: CaseStudy,
  ContentfulCta: Cta,
  ContentfulCtaChecklist: CtaChecklist,
  ContentfulDeveloperArea: DeveloperArea,
  ContentfulTrialFormArea: TrialFormArea,
  ContentfulDownloadCards: DownloadCards,
  ContentfulDownloadTabs: DownloadTabs,
  ContentfulFeaturedCards: FeaturedCards,
  ContentfulFeaturedContent: FeaturedContent,
  ContentfulFeaturedResources: FeaturedResources,
  ContentfulFeaturesTable: FeaturesTableBlock,
  ContentfulHeader: Header,
  ContentfulHero: Hero,
  ContentfulIconCards: IconCards,
  ContentfulIconColumns: IconColumns,
  ContentfulInlineNavigation: InlineNav,
  ContentfulLinkCards: LinkCards,
  ContentfulList: List,
  ContentfulLogoList: LogoList,
  ContentfulMediaTabs: MediaTabs,
  ContentfulMiniCta: MiniCta,
  ContentfulMiniHeader: MiniHeader,
  ContentfulOffsetCards: OffsetCards,
  ContentfulOverlay: Overlay,
  ContentfulOverviewTabs: OverviewTabs,
  ContentfulPhotoHeader: PhotoHeader,
  ContentfulPricingTabs: PricingTabs,
  ContentfulPricingTable: PricingTable,
  ContentfulPromoBanner: PromoBanner,
  ContentfulResourceLinks: ResourceLinks,
  ContentfulResourceRows: ResourceRows,
  ContentfulScrollSync: ScrollSync,
  ContentfulSocialProof: SocialProof,
  ContentfulSplitHero: SplitHero,
  ContentfulStatGrid: StatGrid,
  ContentfulStatsBadges: StatsBadges,
  ContentfulStackedHeader: StackedHeader,
  ContentfulTabs: Tabs,
  ContentfulTestimonials: Testimonials,
  ContentfulText: Text,
  ContentfulTextMedia: TextMedia,
  ContentfulTwoColumnText: TwoColumnText,
  ContentfulVerticalTabs: VerticalTabs,
};

const FlexibleBlock = ({ type, data: { personalizations = null, ...data }, useH1 }) => {
  const Component = blockComponents[type];
  if (!Component) {
    return null;
  }

  if (typeof Component !== 'function') {
    throw new TypeError(
      `Block component for type "${type}" is not a function`
    );
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  const block = <Component {...data} useH1={useH1} />;

  if (!isValidElement(block)) {
    throw new TypeError(
      `Block component for type "${type}" is not a valid React element`
    );
  }

  return (
    <Personalization options={personalizations}>{block}</Personalization>
  );
};

FlexibleBlock.propTypes = {
  type: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  useH1: PropTypes.bool
};

export const query = graphql`
  fragment FlexibleBlockFragment on ContentBlockUnion {
    ...AccordionFragment
    ...AnimatedHeroFragment
    ...BreadcrumbsBlockFragment
    ...CardSliderFragment
    ...CaseStudyFragment
    ...CtaChecklistFragment
    ...CtaFragment
    ...DeveloperAreaBlockFragment
    ...TrialFormAreaBlockFragment
    ...DownloadCardsFragment
    ...DownloadTabsFragment
    ...FeaturedCardsFragment
    ...FeaturedContentFragment
    ...FeaturedResourcesFragment
    ...FeaturesTableFragment
    ...HeaderFragment
    ...HeroFragment
    ...IconCardsFragment
    ...IconColumnsFragment
    ...InlineNavigationFragment
    ...LinkCardsFragment
    ...ListFragment
    ...LogoListFragment
    ...MiniCtaFragment
    ...MiniHeaderFragment
    ...OffsetCardsFragment
    ...OverlayFragment
    ...OverviewTabsFragment
    ...PhotoHeaderFragment
    ...PricingTabsFragment
    ...PricingTableFragment
    ...PromoBannerFragment
    ...ResourceLinksFragment
    ...ScrollSyncFragment
    ...SocialProofFragment
    ...SplitHeroFragment
    ...StatGridFragment
    ...StackedHeaderFragment
    ...StatsBadgesFragment
    ...TabsFragment
    ...TestimonialsFragment
    ...TextFragment
    ...TextMediaFragment
    ...TwoColumnTextFragment
    ...VerticalTabsFragment
    ...MediaTabsFragment
    ...ResourceRowsFragment
    # New blocks need to be added to custom type definitions in gatsby-node.js
  }
`;

export default FlexibleBlock;
